import { useState, useEffect, useCallback } from "react";
import { MeResponse } from "@retrospecter/shared/types";
import {
  apiGet,
  apiPost,
  clearCsrfToken,
  UnauthorizedError,
  AuthProviderUnavailableError,
} from "../api/client";

export type LoginFailureReason =
  | "invalid_credentials"
  | "provider_unavailable"
  | "unknown";

export type LoginOutcome =
  | { ok: true }
  | { ok: false; reason: LoginFailureReason };

export interface AuthState {
  staff?: { username: string };
  participant?: {
    retrospectivePublicId: string;
    participantId: string;
    displayName: string;
  };
  loading: boolean;
  error?: string;
}

export function useAuth() {
  const [auth, setAuth] = useState<AuthState>({ loading: true });

  /**
   * Re-reads the session. Needed after any action that changes who the caller
   * is — notably joining a board, which creates a participant session the page
   * would otherwise not know about until a reload.
   */
  const refresh = useCallback(async () => {
    try {
      const data: MeResponse = await apiGet("/api/auth/me");
      setAuth({
        staff: data.staff,
        participant: data.participant,
        loading: false,
      });
    } catch (error) {
      console.error("Failed to fetch auth:", error);
      setAuth({ loading: false });
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (
    username: string,
    password: string
  ): Promise<LoginOutcome> => {
    try {
      const data = await apiPost("/api/auth/login", { username, password });

      // Logging in rotates the session's CSRF secret, so anything cached from
      // before the login is now stale.
      clearCsrfToken();

      setAuth({
        staff: data.staff,
        loading: false,
      });
      return { ok: true };
    } catch (error) {
      // SPEC §12: bad credentials and an unreachable auth provider are
      // different failures and must not be reported identically.
      const reason: LoginFailureReason =
        error instanceof AuthProviderUnavailableError
          ? "provider_unavailable"
          : error instanceof UnauthorizedError
          ? "invalid_credentials"
          : "unknown";

      setAuth((prev) => ({ ...prev, loading: false, error: reason }));
      return { ok: false, reason };
    }
  };

  const logout = async () => {
    try {
      await apiPost("/api/auth/logout", {});
      clearCsrfToken();
      setAuth({ loading: false });
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  return { auth, login, logout, refresh };
}
